import type { FC } from 'react'
import type { Viewer } from '@/types'

import Link from 'next/link'

import {
    Bookmark,
    FileText,
    KanbanSquare,
    LayoutDashboard,
    LogOut,
    Settings,
    Shield
} from 'lucide-react'

import { initials } from '@/lib/text'
import { cms } from '@/lib/cms'
import { useSignOut } from '@/hooks/use-sign-out'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

interface UserMenuProps {
    viewer: Viewer
}

export const UserMenu: FC<UserMenuProps> = ({ viewer }) => {
    const { signOut, pending } = useSignOut()

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className='flex cursor-pointer items-center gap-2 rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/40'
                aria-label='Open account menu'
            >
                <Avatar>
                    <AvatarFallback>{initials(viewer.name)}</AvatarFallback>
                </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-60'>
                <DropdownMenuGroup>
                    <DropdownMenuLabel className='flex flex-col gap-0.5 py-2'>
                        <span className='truncate text-sm font-semibold text-foreground' data-mdlfy-cms={cms('users', viewer.id, 'name')}>{viewer.name}</span>
                        <span className='truncate text-xs font-normal' data-mdlfy-cms={cms('users', viewer.id, 'email')}>{viewer.email}</span>
                    </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem render={<Link href='/dashboard' />}>
                    <LayoutDashboard />
                    Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem render={<Link href='/resumes' />}>
                    <FileText />
                    My resumes
                </DropdownMenuItem>
                <DropdownMenuItem render={<Link href='/saved' />}>
                    <Bookmark />
                    Saved jobs
                </DropdownMenuItem>
                <DropdownMenuItem render={<Link href='/applications' />}>
                    <KanbanSquare />
                    Applications
                </DropdownMenuItem>
                <DropdownMenuItem render={<Link href='/settings' />}>
                    <Settings />
                    Settings
                </DropdownMenuItem>
                {viewer.isAdmin && (
                    <DropdownMenuItem render={<Link href='/admin' />}>
                        <Shield />
                        Admin workspace
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={pending} onClick={() => void signOut()}>
                    <LogOut />
                    Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
